import { useQuery, useQueryClient } from '@tanstack/react-query';
import { transcriptPlayerKeys } from 'app/queries/transcriptPlayer/transcriptPlayerKeys';
import { createFetchTranscriptQueryFn } from 'app/queries/transcriptPlayer/createFetchTranscriptQueryFn';
import { createFetchTranscriptAudioQueryFn } from 'app/queries/transcriptPlayer/createFetchTranscriptAudioQueryFn';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { GetJobUtterancesResponseDto } from 'app/API';
import { useAPI } from '@providers/useAPI';
import { ASR_MODEL_UNKNOWN } from '@pages/Admin/TranscriptPlayer/helpers/getAsrModel';

type TState = {
  jobId: string;
  error: string | null;
  isLoading: boolean;
  utterances: GetJobUtterancesResponseDto['utterances'];
  availableTranscripts: GetJobUtterancesResponseDto['availableTranscripts'];
  transcriptFile: string;
  setTranscriptFile: (filename: string) => void;
  audioSource: { url: string; attempt: number } | null;
  audioError: string | null;
  asrModel: string | null;
  refreshAudioUrl: () => void;
  triggerManualReload: () => void;
};

const PageContext = createContext<TState>({
  jobId: '',
  error: null,
  isLoading: false,
  utterances: [],
  availableTranscripts: [],
  transcriptFile: 'asr.json',
  setTranscriptFile: () => {},
  audioSource: null,
  audioError: null,
  asrModel: null,
  refreshAudioUrl: () => {},
  triggerManualReload: () => {},
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const params = useParams() as { id: string };
  const queryClient = useQueryClient();
  const [audioReloadToken, setAudioReloadToken] = useState(0);
  const [searchParams] = useSearchParams();
  const [transcriptFile, setTranscriptFile] = useState<string>(searchParams.get('transcript') ?? 'asr.json');
  const jobId = params?.id ?? '';
  const [catalog, setCatalog] = useState<{
    jobId: string;
    transcripts: GetJobUtterancesResponseDto['availableTranscripts'];
  } | null>(null);

  const transcriptQuery = useQuery({
    queryKey: transcriptPlayerKeys.detail(jobId, transcriptFile),
    queryFn: createFetchTranscriptQueryFn(call),
    enabled: Boolean(jobId && transcriptFile),
  });
  const audioQuery = useQuery({
    queryKey: transcriptPlayerKeys.audio(jobId, audioReloadToken),
    queryFn: createFetchTranscriptAudioQueryFn(call),
    enabled: Boolean(jobId),
    staleTime: Infinity,
  });
  useEffect(() => {
    if (transcriptQuery.data) {
      setCatalog({ jobId, transcripts: transcriptQuery.data.availableTranscripts });
    }
  }, [jobId, transcriptQuery.data]);
  const isLoading = transcriptQuery.isLoading;
  const utterances = transcriptQuery.data?.utterances ?? [];
  const availableTranscripts =
    transcriptQuery.data?.availableTranscripts ?? (catalog?.jobId === jobId ? catalog.transcripts : []);
  const asrModel = transcriptFile === 'asr.json' ? transcriptQuery.data?.asrModel ?? ASR_MODEL_UNKNOWN : null;
  const error = transcriptQuery.isError ? 'Unable to load the transcript file' : null;
  const audioSource = audioQuery.data?.signedUrl ? { url: audioQuery.data.signedUrl, attempt: audioReloadToken } : null;
  const audioError = audioQuery.isError ? 'Unable to get the signed URL' : null;

  return (
    <PageContext.Provider
      value={{
        jobId,
        isLoading,
        utterances,
        availableTranscripts,
        transcriptFile,
        setTranscriptFile,
        audioSource,
        audioError,
        asrModel,
        error,
        refreshAudioUrl: () => setAudioReloadToken((token) => token + 1),
        triggerManualReload: () => {
          void queryClient.invalidateQueries({ queryKey: transcriptPlayerKeys.details() });
        },
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
